import { useState } from 'react';
import { motion } from 'motion/react';
import { ExternalLink } from 'lucide-react';
import { PortfolioProjectModal } from '../components/PortfolioProjectModal';
import { PortfolioItem } from '../utils/data';
import { getPortfolioImages, normalizeWebsiteUrl, resolveUploadUrl, useSiteConfig } from '../utils/siteConfig';

export function Portfolio() {
  const { config } = useSiteConfig();
  const portfolio = [...config.portfolio].reverse();
  const [selectedProject, setSelectedProject] = useState<PortfolioItem | null>(null);

  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      <div className="mx-auto max-w-7xl px-4 py-24 md:px-8">
        <motion.div
          className="mb-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="mb-4 inline-block rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary shadow-sm">
            Our Work
          </span>
          <h2 className="heading-glow mb-6 text-4xl font-black tracking-tight text-slate-900 md:text-6xl">Success Stories</h2>
          <p className="mx-auto max-w-2xl font-sans text-xl leading-relaxed text-slate-600">
            A showcasing of our craft, creativity, and results-driven strategies built for leading brands globally.
          </p>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {portfolio.map((project, index) => {
            const images = getPortfolioImages(project);
            const websiteUrl = normalizeWebsiteUrl(project.websiteUrl);
            return (
              <motion.article
                key={`${project.title}-${index}`}
                onClick={() => setSelectedProject(project)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedProject(project);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View ${project.title} details`}
                className="group flex h-full cursor-pointer flex-col transition-all duration-300 hover:-translate-y-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-4"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm transition-all duration-300 group-hover:shadow-xl">
                  {images.length > 0 ? (
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={resolveUploadUrl(images[0])}
                        alt={project.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      {images.length > 1 && (
                        <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-slate-950/75 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">
                          {images.length} images
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="h-4 border-b border-slate-100 bg-slate-50" />
                  )}

                  <div className="flex flex-grow flex-col justify-between p-6">
                    <div>
                      <span className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-emerald-500">
                        {project.category}
                      </span>
                      <div className="space-y-3">
                        <h3 className="text-lg font-black leading-snug text-slate-900">{project.title}</h3>
                        {project.tags && (
                          <div className="flex flex-wrap gap-1.5">
                            {project.tags.split(',').map((tag, tagIndex) => (
                              <span key={tagIndex} className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-slate-500">
                                {tag.trim()}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-slate-500">{project.description}</p>
                    {websiteUrl && (
                      <a
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={event => event.stopPropagation()}
                        onKeyDown={event => event.stopPropagation()}
                        className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-primary transition hover:text-accent focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        Visit website <ExternalLink size={15} />
                      </a>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      {selectedProject && (
        <PortfolioProjectModal
          project={selectedProject}
          allProjects={portfolio}
          onSelectProject={setSelectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
}
